import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Check, Clock, MusicNote, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';

const DAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'];

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingToken = searchParams.get('token') || '';
  const [company, setCompany] = useState(null);
  const [step, setStep] = useState(1);
  const [instruments, setInstruments] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [teacherLocked, setTeacherLocked] = useState(false);
  const [acceptedRegolamento, setAcceptedRegolamento] = useState(false);
  const [acceptedListino, setAcceptedListino] = useState(false);
  const docsSectionRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    nome: '', telefono: '', email: '',
    anno_accademico: currentYear,
    strumento: '', teacher_id: '', teacher_name: '',
    durata: 30, giorno: -1, ora_inizio: '', is_prova: false, interested_slots_note: ''
  });

  useEffect(() => {
    if (form.ora_inizio && docsSectionRef.current) {
      setTimeout(() => {
        docsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.ora_inizio, form.giorno]);

  useEffect(() => {
    api.getCompanySettings().then(res => {
      setCompany(res.data);
      // Se l'admin ha fissato un anno accademico per le prenotazioni pubbliche, forzalo (solo per prenotazioni pubbliche, non da token).
      if (!bookingToken && res.data.public_booking_anno_accademico) {
        const forced = parseInt(res.data.public_booking_anno_accademico);
        if (!Number.isNaN(forced)) setForm(prev => ({ ...prev, anno_accademico: forced }));
      }
      // Le prenotazioni sono "chiuse" solo se public_booking_open_at è nel futuro.
      // Se l'alunno arriva con un booking token personale bypassa il blocco.
      if (bookingToken) return;
      const openAtRaw = res.data.public_booking_open_at || '';
      if (openAtRaw) {
        const openAt = new Date(openAtRaw);
        if (!isNaN(openAt.getTime()) && openAt > new Date()) navigate('/');
      }
    }).catch(() => { if (!bookingToken) navigate('/'); });
    api.getPublicInstruments().then(res => setInstruments(res.data)).catch(() => {});
  }, [navigate, bookingToken]);

  // Se presente un token, precompila i dati dell'alunno esistente
  useEffect(() => {
    if (!bookingToken) return;
    api.getBookingPrefill(bookingToken)
      .then(res => {
        setForm(prev => ({
          ...prev,
          nome: res.data.nome || prev.nome,
          telefono: res.data.telefono || prev.telefono,
          email: res.data.email || prev.email,
          // se il token è teacher-specific pre-seleziona strumento + insegnante
          strumento: res.data.strumento || prev.strumento,
          teacher_id: res.data.teacher_id || prev.teacher_id,
          teacher_name: res.data.teacher_name || prev.teacher_name,
        }));
        setPrefilled(true);
        if (res.data.teacher_locked) {
          setTeacherLocked(true);
          // fetch availability subito
          if (res.data.teacher_id) {
            api.getPublicAvailability(res.data.teacher_id, form.anno_accademico)
              .then(r => setAvailability(r.data))
              .catch(() => {});
          }
        }
      })
      .catch(() => { toast.error('Link di prenotazione non valido'); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingToken]);

  const selectedInstr = instruments.find(i => i.strumento === form.strumento);

  const loadAvailability = async (teacherId) => {
    setLoadingSlots(true);
    try {
      const res = await api.getPublicAvailability(teacherId, form.anno_accademico);
      setAvailability(res.data);
    } catch { toast.error('Errore caricamento disponibilità'); }
    finally { setLoadingSlots(false); }
  };

  const handleTeacherSelect = (tid) => {
    const teacher = selectedInstr?.insegnanti.find(t => t.id === tid);
    setForm(prev => ({ ...prev, teacher_id: tid, teacher_name: teacher?.name || '', giorno: -1, ora_inizio: '' }));
    loadAvailability(tid);
  };

  const canSlotFit = (daySlots, startIdx, slotsNeeded) => {
    for (let i = 0; i < slotsNeeded; i++) {
      if (startIdx + i >= daySlots.length) return false;
      if (!daySlots[startIdx + i].available) return false;
    }
    return true;
  };

  const slotsNeeded = form.durata / 15;

  const handleSlotClick = (dayIdx, time) => {
    setForm({ ...form, giorno: dayIdx, ora_inizio: time });
  };

  const handleSubmit = async () => {
    if (!form.nome.trim() || !form.email.trim()) { toast.error('Compila nome e email'); return; }
    if (!form.teacher_id || form.giorno < 0 || !form.ora_inizio) { toast.error('Seleziona uno slot'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (bookingToken) payload.booking_token = bookingToken;
      await api.createBooking(payload);
      setSuccess(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio prenotazione'); }
    finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#2D2A4A] flex items-center justify-center p-6">
        <div className="bg-white rounded-sm p-10 max-w-md text-center" data-testid="booking-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check size={32} className="text-green-600" /></div>
          <h2 className="text-2xl font-bold font-['Work_Sans'] mb-2">Prenotazione Inviata!</h2>
          <p className="text-[#52525B] mb-1">Riceverai una email di conferma a <strong>{form.email}</strong></p>
          <p className="text-sm text-[#A1A1AA] mb-6">Ti contatteremo a breve per confermare la tua iscrizione.</p>
          <Button onClick={() => navigate('/')} className="bg-[#2D2A4A] rounded-sm">Torna alla Home</Button>
        </div>
      </div>
    );
  }

  // Gate finestra temporale per link con token
  const parseDT = (s) => { if (!s) return null; const d = new Date(s); return isNaN(d.getTime()) ? null : d; };
  const now = new Date();
  const openAt = bookingToken ? parseDT(company?.token_booking_open_at) : null;
  const closeAt = bookingToken ? parseDT(company?.token_booking_close_at) : null;
  const notYetOpen = !!(openAt && now < openAt);
  const alreadyClosed = !!(closeAt && now > closeAt);
  const formatDT = (d) => d ? d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  if (bookingToken && company && (notYetOpen || alreadyClosed)) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center px-6" data-testid="booking-token-gate">
        <div className="max-w-md w-full bg-white border border-[#E4E4E7] rounded-sm p-8 text-center shadow-sm">
          <div className="mx-auto w-14 h-14 bg-[#2D2A4A] text-white rounded-full flex items-center justify-center mb-4"><Clock size={28} /></div>
          <h1 className="text-xl font-bold text-[#2D2A4A] font-['Work_Sans'] mb-2">
            {notYetOpen ? 'Prenotazioni non ancora aperte' : 'Prenotazioni chiuse'}
          </h1>
          {notYetOpen ? (
            <p className="text-sm text-[#52525B]">Le prenotazioni apriranno il<br/><strong className="text-[#0A0A0A] text-base">{formatDT(openAt)}</strong></p>
          ) : (
            <p className="text-sm text-[#52525B]">Le prenotazioni si sono chiuse il<br/><strong className="text-[#0A0A0A] text-base">{formatDT(closeAt)}</strong><br/><br/>Per informazioni contatta la segreteria.</p>
          )}
          {closeAt && notYetOpen && (
            <p className="text-xs text-[#A1A1AA] mt-4">Chiusura prevista: {formatDT(closeAt)}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <div className="bg-[#2D2A4A] text-white px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/')} className="hover:bg-white/10 p-2 rounded transition-colors" data-testid="booking-back"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="text-lg font-bold font-['Work_Sans']">{company?.nome || 'Fiori Musicali'}</h1>
          <p className="text-white/60 text-xs">Nuova Prenotazione Lezione</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-6">
          {['Dati Personali', 'Strumento', 'Orario'].map((label, i) => (
            <div key={i} className={`flex items-center gap-2 ${i > 0 ? 'ml-2' : ''}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-[#2D2A4A] text-white' : 'bg-[#E4E4E7] text-[#A1A1AA]'}`}>{step > i + 1 ? <Check size={14} /> : i + 1}</div>
              <span className={`text-sm ${step === i + 1 ? 'font-semibold text-[#0A0A0A]' : 'text-[#A1A1AA]'}`}>{label}</span>
              {i < 2 && <div className="w-8 h-px bg-[#E4E4E7] ml-2" />}
            </div>
          ))}
        </div>

        {/* Step 1: Dati personali */}
        {step === 1 && (
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 space-y-4" data-testid="booking-step-1">
            <h2 className="text-lg font-bold font-['Work_Sans']">Dati Personali</h2>
            {prefilled && (
              <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-sm text-green-800 flex items-start gap-2" data-testid="booking-prefilled-banner">
                <Check size={18} className="text-green-600 mt-0.5 shrink-0" />
                <div>
                  Bentornato/a <strong>{form.nome.toUpperCase()}</strong>! I tuoi dati sono già pre-compilati.
                  {teacherLocked && form.teacher_name ? (
                    <> Prenoterai una lezione con <strong>{form.teacher_name}</strong>{form.strumento ? <> ({form.strumento})</> : null}.</>
                  ) : <> Vai avanti per scegliere strumento, insegnante e orario.</>}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label className="label-overline">Cognome e Nome *</Label>
                <Input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="es. Rossi Mario" className="rounded-sm uppercase" data-testid="booking-nome" required />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Telefono</Label>
                <Input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} placeholder="es. 333 1234567" className="rounded-sm" data-testid="booking-telefono" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="es. mario@email.it" className="rounded-sm" data-testid="booking-email" required />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Anno Accademico</Label>
                {(() => {
                  const forcedYear = !bookingToken && company?.public_booking_anno_accademico ? parseInt(company.public_booking_anno_accademico) : null;
                  if (forcedYear) {
                    return (
                      <div className="rounded-sm border border-input bg-[#F4F4F5] px-3 py-2 text-sm font-medium" data-testid="booking-anno-locked">
                        {forcedYear} / {String(forcedYear + 1).slice(-2)}
                      </div>
                    );
                  }
                  return (
                    <Select value={form.anno_accademico.toString()} onValueChange={v => setForm({ ...form, anno_accademico: parseInt(v) })}>
                      <SelectTrigger className="rounded-sm" data-testid="booking-anno"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[currentYear - 1, currentYear, currentYear + 1].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => { if (!form.nome.trim() || !form.email.trim()) { toast.error('Compila nome e email'); return; } setStep(teacherLocked ? 3 : 2); }} className="bg-[#2D2A4A] rounded-sm" data-testid="booking-next-1">Avanti</Button>
            </div>
          </div>
        )}

        {/* Step 2: Strumento e insegnante */}
        {step === 2 && (
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 space-y-4" data-testid="booking-step-2">
            <h2 className="text-lg font-bold font-['Work_Sans']">Scegli Strumento</h2>
            <div className="space-y-2">
              <Label className="label-overline">Strumento</Label>
              <Select value={form.strumento} onValueChange={v => setForm({ ...form, strumento: v, teacher_id: '', teacher_name: '', giorno: -1, ora_inizio: '' })}>
                <SelectTrigger className="rounded-sm" data-testid="booking-strumento"><SelectValue placeholder="Seleziona strumento..." /></SelectTrigger>
                <SelectContent>{instruments.map(i => <SelectItem key={i.strumento} value={i.strumento}>{i.strumento}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selectedInstr && (
              <div className="space-y-2">
                <Label className="label-overline">Insegnante</Label>
                <Select value={form.teacher_id} onValueChange={handleTeacherSelect}>
                  <SelectTrigger className="rounded-sm" data-testid="booking-teacher"><SelectValue placeholder="Seleziona insegnante..." /></SelectTrigger>
                  <SelectContent>{selectedInstr.insegnanti.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-overline">Durata Lezione</Label>
              <div className="flex gap-2 flex-wrap">
                {[30, 45, 60].map(d => (
                  <button key={d} onClick={() => { setForm({ ...form, durata: d, is_prova: false, giorno: -1, ora_inizio: '' }); if (form.teacher_id) loadAvailability(form.teacher_id); }}
                    className={`flex-1 min-w-[80px] py-2 rounded-sm text-sm font-medium border transition-colors ${form.durata === d && !form.is_prova ? 'bg-[#2D2A4A] text-white border-[#2D2A4A]' : 'bg-white text-[#52525B] border-[#E4E4E7] hover:bg-[#F4F4F5]'}`}
                    data-testid={`booking-durata-${d}`}>
                    <Clock size={14} className="inline mr-1" />{d} min
                  </button>
                ))}
                {!teacherLocked && (
                  <button onClick={() => { setForm({ ...form, durata: 30, is_prova: true, giorno: -1, ora_inizio: '' }); if (form.teacher_id) loadAvailability(form.teacher_id); }}
                    className={`flex-1 min-w-[100px] py-2 rounded-sm text-sm font-bold border transition-colors uppercase tracking-wider ${form.is_prova ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-400 hover:bg-amber-50'}`}
                    data-testid="booking-durata-prova">
                    ★ Prova
                  </button>
                )}
              </div>
              {form.is_prova && (
                <p className="text-xs text-amber-700 italic">Lezione di prova (30 minuti). L'insegnante ti confermerà orario e modalità.</p>
              )}
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm">Indietro</Button>
              <Button onClick={() => { if (!form.strumento || !form.teacher_id) { toast.error('Seleziona strumento e insegnante'); return; } setStep(3); }} className="bg-[#2D2A4A] rounded-sm" data-testid="booking-next-2" disabled={!form.teacher_id}>Avanti</Button>
            </div>
          </div>
        )}

        {/* Step 3: Slot orario */}
        {step === 3 && (
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-6 space-y-4" data-testid="booking-step-3">
            <h2 className="text-lg font-bold font-['Work_Sans']">Scegli Orario</h2>

            {/* Riepilogo + selettore durata sempre visibile */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pb-3 border-b border-[#E4E4E7]">
              <div className="flex-1">
                <div className="text-xs text-[#71717A] uppercase tracking-wider font-semibold">Insegnante</div>
                <div className="text-sm font-medium">
                  {form.teacher_name?.toUpperCase()} {form.strumento && <span className="text-[#71717A] font-normal">({form.strumento})</span>}
                </div>
              </div>
              <div className="w-full sm:w-auto">
                <Label className="label-overline text-xs">Durata Lezione</Label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {[30, 45, 60].map(d => (
                    <button key={d} onClick={() => { setForm({ ...form, durata: d, is_prova: false, giorno: -1, ora_inizio: '' }); if (form.teacher_id) loadAvailability(form.teacher_id); }}
                      className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${form.durata === d && !form.is_prova ? 'bg-[#2D2A4A] text-white border-[#2D2A4A]' : 'bg-white text-[#52525B] border-[#E4E4E7] hover:bg-[#F4F4F5]'}`}
                      data-testid={`booking-step3-durata-${d}`}>
                      <Clock size={12} className="inline mr-1" />{d} min
                    </button>
                  ))}
                  {!teacherLocked && (
                    <button onClick={() => { setForm({ ...form, durata: 30, is_prova: true, giorno: -1, ora_inizio: '' }); if (form.teacher_id) loadAvailability(form.teacher_id); }}
                      className={`px-3 py-1.5 rounded-sm text-xs font-bold border transition-colors uppercase tracking-wider ${form.is_prova ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-400 hover:bg-amber-50'}`}
                      data-testid="booking-step3-durata-prova">
                      ★ Prova
                    </button>
                  )}
                </div>
              </div>
              {!teacherLocked && selectedInstr?.insegnanti?.length > 1 && (
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Label className="label-overline text-xs whitespace-nowrap">Cambia insegnante:</Label>
                  <Select value={form.teacher_id} onValueChange={handleTeacherSelect}>
                    <SelectTrigger className="rounded-sm w-[180px] h-9 text-sm" data-testid="booking-step3-teacher"><SelectValue /></SelectTrigger>
                    <SelectContent>{selectedInstr?.insegnanti.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {loadingSlots ? (
              <div className="text-center py-8 text-[#52525B]">Caricamento disponibilità...</div>
            ) : (
              <>
                <div className="flex items-center gap-4 flex-wrap text-[10px] text-[#52525B]">
                  <div className="flex items-center gap-1"><div className="w-4 h-4 bg-white border border-[#E4E4E7] rounded-sm" /> Libero &amp; prenotabile</div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 bg-slate-100 border border-slate-300 rounded-sm" /> Non prenotabile</div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 bg-amber-100 border border-amber-300 rounded-sm" /> In attesa pagamento (potrebbe liberarsi)</div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 bg-orange-100 border border-orange-300 rounded-sm" /> Prova (potrebbe liberarsi)</div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 bg-red-200 border border-red-400 rounded-sm" /> Confermato pagato</div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 bg-white border border-[#E4E4E7] rounded-sm flex items-center justify-center text-[#D4D4D8] font-bold">·</div> Libero ma non basta per la durata scelta</div>
                  <div className="flex items-center gap-1"><div className="w-4 h-4 bg-[#2D2A4A] rounded-sm" /> Selezionato</div>
                </div>
                <p className="text-[10px] text-[#71717A] italic">Gli slot &quot;In attesa pagamento&quot; e &quot;Prova&quot; potrebbero liberarsi. Se molte caselle risultano grigie, prova a ridurre la durata della lezione dal menù &quot;Durata&quot; sopra.</p>
                <div className="overflow-x-auto">
                  <div className="grid grid-cols-5 gap-1 min-w-[600px]">
                    {DAYS.map((day, dayIdx) => (
                      <div key={dayIdx} className="text-center">
                        <div className="text-xs font-semibold text-[#52525B] py-2 bg-[#F4F4F5] rounded-t-sm">{day}</div>
                        <div className="space-y-px bg-[#F4F4F5] rounded-b-sm p-px">
                          {availability[dayIdx]?.slots.map((slot, slotIdx) => {
                            const fits = canSlotFit(availability[dayIdx].slots, slotIdx, slotsNeeded);
                            const isSelected = form.giorno === dayIdx && form.ora_inizio === slot.time;
                            const isInRange = form.giorno === dayIdx && form.ora_inizio && (() => {
                              const [sh, sm] = form.ora_inizio.split(':').map(Number);
                              const [ch, cm] = slot.time.split(':').map(Number);
                              const startMin = sh * 60 + sm;
                              const curMin = ch * 60 + cm;
                              return curMin >= startMin && curMin < startMin + form.durata;
                            })();
                            if (!slot.available) {
                              const bs = slot.booking_stato;
                              const flags = new Set(slot.lesson_flags || []);
                              // Priorità: disabled dall'insegnante > R1 pagato > lezione/booking in attesa
                              const isDisabled = slot.is_disabled && !bs && flags.size === 0;
                              const isPaid = flags.has('r1');
                              const isProvaPending = !isDisabled && !isPaid && bs && slot.is_prova;
                              // In attesa = c'è una lezione/booking ma R1 non spuntato
                              const isPending = !isDisabled && !isPaid && (bs || flags.size > 0 || true) && !isProvaPending;
                              const bsCls = isDisabled ? 'bg-slate-100 text-slate-600 border-slate-300'
                                : isPaid ? 'bg-red-200 text-red-800 border-red-400'
                                : isProvaPending ? 'bg-orange-100 text-orange-700 border-orange-300'
                                : 'bg-amber-100 text-amber-700 border-amber-300';
                              const bsLbl = isDisabled ? 'Non prenot.'
                                : isPaid ? 'Conf. Pagato'
                                : isProvaPending ? 'Prova'
                                : 'In attesa';
                              const fullLbl = isDisabled ? 'Non prenotabile'
                                : isPaid ? 'Confermato pagato'
                                : isProvaPending ? 'Prova (potrebbe liberarsi)'
                                : 'In attesa pagamento (potrebbe liberarsi)';
                              return (
                                <div key={slot.time} className={`h-7 border ${bsCls} text-[9px] flex items-center justify-center font-medium leading-tight`} title={`${fullLbl} - ${slot.time}`}>
                                  <span>{slot.time}<br/><span className="text-[8px] font-bold uppercase">{bsLbl}</span></span>
                                </div>
                              );
                            }
                            return (
                              <button key={slot.time} onClick={() => fits && handleSlotClick(dayIdx, slot.time)} disabled={!fits}
                                title={fits ? `Prenotabile: ${slot.time} - durata ${form.durata} min` : `Libero ma non c'è spazio consecutivo per ${form.durata} min - prova a ridurre la durata`}
                                className={`w-full h-7 text-[10px] font-medium transition-colors ${isSelected || isInRange ? 'bg-[#2D2A4A] text-white' : fits ? 'bg-white hover:bg-green-50 text-[#52525B]' : 'bg-white text-[#D4D4D8] cursor-not-allowed'}`}>
                                {slot.time}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Nota interesse slot che potrebbero liberarsi */}
            <div className="space-y-2 pt-2" data-testid="booking-interest-note-section">
              <Label className="label-overline text-xs">Cosa potrebbe interessarti degli slot che potrebbero liberarsi?</Label>
              <Textarea
                value={form.interested_slots_note || ''}
                onChange={(e) => setForm({ ...form, interested_slots_note: e.target.value })}
                placeholder="Facoltativo. Es. Sarei interessato al Martedì tra le 16:30 e le 18:00 se si libera."
                rows={2}
                className="rounded-sm text-sm"
                data-testid="booking-interest-note"
              />
              <p className="text-[10px] text-[#A1A1AA]">Se non trovi lo slot ideale, indica qui gli orari 'in attesa/prova' che ti interesserebbero: verrai contattato se si liberano.</p>
            </div>

            {form.ora_inizio && (
              <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-sm text-green-800" data-testid="booking-selection-summary">
                Selezionato: <strong>{DAYS[form.giorno]}</strong> dalle <strong>{form.ora_inizio}</strong> per <strong>{form.durata} min</strong>{form.is_prova && <span className="ml-2 inline-block px-2 py-0.5 bg-amber-500 text-white text-xs font-bold uppercase tracking-wide rounded">Prova</span>}
              </div>
            )}

            {form.ora_inizio && (company?.regolamento_url || company?.listino_url) && (
              <div ref={docsSectionRef} className="space-y-3 pt-3 border-t-2 border-amber-400 bg-amber-50 -mx-4 px-4 pb-3 rounded-b-sm" data-testid="booking-docs-section">
                <p className="text-sm font-bold text-amber-900 uppercase tracking-wide">⚠ Prima di inviare: leggi e accetta i documenti</p>
                {[
                  { url: company.regolamento_url, label: 'Regolamento Accademia', state: acceptedRegolamento, setter: setAcceptedRegolamento, key: 'regolamento' },
                  { url: company.listino_url, label: 'Listino Prezzi', state: acceptedListino, setter: setAcceptedListino, key: 'listino' },
                ].filter(d => d.url).map(d => (
                  <div key={d.key} className="border border-[#E4E4E7] rounded-sm p-3 space-y-2" data-testid={`booking-doc-${d.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <FilePdf size={20} className="text-red-600" weight="duotone" />
                        {d.label}
                      </span>
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-[#2D2A4A] underline hover:no-underline" data-testid={`booking-view-${d.key}-btn`}>
                        Visualizza PDF
                      </a>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox checked={d.state} onCheckedChange={(v) => d.setter(!!v)} data-testid={`booking-accept-${d.key}-checkbox`} className="mt-0.5" />
                      <span className="text-xs text-[#52525B]">Confermo di aver letto il <strong>{d.label}</strong>.</span>
                    </label>
                  </div>
                ))}
                {form.ora_inizio && !(acceptedRegolamento && acceptedListino) && (company?.regolamento_url && company?.listino_url) && (
                  <p className="text-xs text-[#A1A1AA] italic">Visualizza i due PDF e spunta le caselle qui sopra per attivare l&apos;invio.</p>
                )}
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(teacherLocked ? 1 : 2)} className="rounded-sm">Indietro</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !form.ora_inizio || (company?.regolamento_url && !acceptedRegolamento) || (company?.listino_url && !acceptedListino)}
                className="bg-[#2D2A4A] rounded-sm"
                data-testid="booking-submit"
              >
                {submitting ? 'Invio...' : 'Invia Prenotazione'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
